import { DateFormat, diff, isDateRangeValid } from '@equip.health/ui';
import { DateTime } from 'luxon';

import {
  AdminEpisode,
  CareTeamType,
  EpisodeInsuranceVerificationStatus,
  EpisodeMedicalClearanceStatus,
  EpisodePsychClearanceStatus,
  EpisodeStatus,
} from '../constants/patients.constants';
import { CarePlanGoalStatus } from '~/lib/types/carePlan/CarePlanGoalStatus';
import { CarePlanInterventionStatus } from '~/lib/types/carePlan/CarePlanInterventionStatus';
import { ProblemTypeCode } from '~/lib/types/carePlan/ProblemTypeCode';
import type {
  CarePlanProblemResponse,
  CarePlanNoteProblemResponse,
  CarePlanQolGoalResponse,
  CarePlanNoteQolGoalResponse,
  ProviderTypeManifestResponse,
} from '~/lib/types/carePlan/carePlans';

export const getAuthorizationDateLabel = (
  label: string,
  isAuthRequired: string,
): string => `${label}${isAuthRequired === 'No' ? ' (Internal)' : ''}`;

export const getFormattedEpisodeDate = (authDate: string): string => {
  return authDate
    ? DateTime.fromFormat(authDate, DateFormat.longDate).toFormat(
        DateFormat.shortYearMonthDay,
      )
    : '';
};

export const isEpisodeStatusRequired = (
  episodeStatus: EpisodeStatus,
): boolean => {
  return [
    EpisodeStatus.ASSESSING,
    EpisodeStatus.ENROLLED,
    EpisodeStatus.IN_TREATMENT,
    EpisodeStatus.PAUSED,
  ].includes(episodeStatus);
};

export const isInsuranceVerificationRequestedDateRequired = (
  insuranceVerificationStatus: EpisodeInsuranceVerificationStatus,
): boolean =>
  [
    EpisodeInsuranceVerificationStatus.READY_FOR_REVIEW,
    EpisodeInsuranceVerificationStatus.VERIFICATION_IN_PROGRESS,
    EpisodeInsuranceVerificationStatus.PRE_AUTHORIZATION_IN_PROGRESS,
    EpisodeInsuranceVerificationStatus.APPROVED,
    EpisodeInsuranceVerificationStatus.DENIED,
  ].includes(insuranceVerificationStatus);

export const isInsuranceVerificationCompletedDateRequired = (
  insuranceVerificationStatus: EpisodeInsuranceVerificationStatus,
): boolean =>
  [
    EpisodeInsuranceVerificationStatus.APPROVED,
    EpisodeInsuranceVerificationStatus.DENIED,
  ].includes(insuranceVerificationStatus);

export const isMedicalClearanceRequestedDateRequired = (
  medicalClearanceStatus: EpisodeMedicalClearanceStatus,
): boolean =>
  [
    EpisodeMedicalClearanceStatus.READY_FOR_REVIEW,
    EpisodeMedicalClearanceStatus.IN_PROGRESS,
    EpisodeMedicalClearanceStatus.CLEARED,
    EpisodeMedicalClearanceStatus.NOT_CLEARED,
  ].includes(medicalClearanceStatus);

export const isPsychClearanceRequestedDateRequired = (
  psychClearanceStatus: EpisodePsychClearanceStatus,
): boolean =>
  [
    EpisodePsychClearanceStatus.READY_FOR_REVIEW,
    EpisodePsychClearanceStatus.IN_PROGRESS,
    EpisodePsychClearanceStatus.CLEARED,
    EpisodePsychClearanceStatus.NOT_CLEARED,
  ].includes(psychClearanceStatus);

export const isMedicalClearanceCompletedDateRequired = (
  medicalClearanceStatus: EpisodeMedicalClearanceStatus,
): boolean =>
  [
    EpisodeMedicalClearanceStatus.CLEARED,
    EpisodeMedicalClearanceStatus.NOT_CLEARED,
  ].includes(medicalClearanceStatus);

export const isPsychClearanceCompletedDateRequired = (
  psychClearanceStatus: EpisodePsychClearanceStatus,
): boolean =>
  [
    EpisodePsychClearanceStatus.CLEARED,
    EpisodePsychClearanceStatus.NOT_CLEARED,
  ].includes(psychClearanceStatus);

export const isAuthPayorFieldsRequired = (
  episodeStatus: EpisodeStatus,
  insuranceVerificationStatus: EpisodeInsuranceVerificationStatus,
): boolean =>
  isEpisodeStatusRequired(episodeStatus) ||
  isInsuranceVerificationRequestedDateRequired(insuranceVerificationStatus);

export const isAdmitDateRequired = (
  episodeStatus: EpisodeStatus,
  insuranceVerificationStatus: EpisodeInsuranceVerificationStatus,
  medicalClearanceStatus: EpisodeMedicalClearanceStatus,
  pausedDetails: EpisodePausedDetails[],
  psychClearanceStatus: EpisodePsychClearanceStatus,
): boolean =>
  isEpisodeStatusRequired(episodeStatus) ||
  isInsuranceVerificationRequestedDateRequired(insuranceVerificationStatus) ||
  isMedicalClearanceRequestedDateRequired(medicalClearanceStatus) ||
  isPsychClearanceRequestedDateRequired(psychClearanceStatus) ||
  pausedDetails?.length > 0;
export const formatEpisodeDate = (episodeDate: Date): string =>
  episodeDate
    ? DateTime.fromJSDate(episodeDate).toFormat(DateFormat.shortYearMonthDay)
    : '';

export const isDateRangeValidExcludingSameDate = (
  fromDate: string,
  toDate: string,
  formatFrom = 'yyyy-LL-dd',
): boolean =>
  diff(
    DateTime.fromFormat(fromDate, formatFrom)
      .startOf('day')
      .toFormat('LL/dd/yyyy HH:mm:ss'),
    DateTime.fromFormat(toDate, formatFrom)
      .startOf('day')
      .toFormat('LL/dd/yyyy HH:mm:ss'),
    'LL/dd/yyyy HH:mm:ss',
  ) > 0;
export const checkForActiveEpisode = (
  externalId: string,
  patientEpisodes: AdminEpisode[],
): boolean =>
  patientEpisodes?.some(
    ({ episodeExternalId, episodeStatus }) =>
      externalId !== episodeExternalId &&
      episodeStatus !== EpisodeStatus.CLOSED,
  );

export const checkForOverlappingEpisodeDates = (
  closedDate: Date,
  externalId: string,
  firstInquiryReferralDate: Date,
  patientEpisodes: AdminEpisode[],
): boolean =>
  patientEpisodes?.some(
    ({
      episodeClosedDate,
      episodeExternalId,
      firstInquiryReferralDate: episodeFirstInquiryReferralDate,
    }) => {
      const formattedFirstInquiryReferralDate = formatEpisodeDate(
        firstInquiryReferralDate,
      );
      const formattedClosedDate = formatEpisodeDate(closedDate);
      if (externalId !== episodeExternalId && episodeFirstInquiryReferralDate) {
        if (!episodeClosedDate) {
          return !(
            isDateRangeValidExcludingSameDate(
              formattedFirstInquiryReferralDate,
              episodeFirstInquiryReferralDate,
            ) &&
            isDateRangeValidExcludingSameDate(
              formattedClosedDate,
              episodeFirstInquiryReferralDate,
            )
          );
        }
        if (!closedDate) {
          return !(
            isDateRangeValidExcludingSameDate(
              episodeFirstInquiryReferralDate,
              formattedFirstInquiryReferralDate,
            ) &&
            isDateRangeValidExcludingSameDate(
              episodeClosedDate,
              formattedFirstInquiryReferralDate,
            )
          );
        }

        return (
          (isDateRangeValid(
            episodeFirstInquiryReferralDate,
            formattedFirstInquiryReferralDate,
          ) &&
            isDateRangeValid(
              formattedFirstInquiryReferralDate,
              episodeClosedDate,
            )) ||
          (isDateRangeValid(
            episodeFirstInquiryReferralDate,
            formattedClosedDate,
          ) &&
            isDateRangeValid(formattedClosedDate, episodeClosedDate)) ||
          (isDateRangeValid(
            formattedFirstInquiryReferralDate,
            episodeFirstInquiryReferralDate,
          ) &&
            isDateRangeValid(
              episodeFirstInquiryReferralDate,
              formattedClosedDate,
            )) ||
          (isDateRangeValid(
            formattedFirstInquiryReferralDate,
            episodeClosedDate,
          ) &&
            isDateRangeValid(episodeClosedDate, formattedClosedDate))
        );
      }
      return false;
    },
  );

export const checkForEpisodeClosedWithinTwoWeeks = (
  firstInquiryReferralDate: Date,
  patientEpisodes: AdminEpisode[],
): boolean =>
  patientEpisodes?.some(
    ({
      episodeClosedDate: existingEpisodeClosedDate,
      firstInquiryReferralDate: existingEpisodeFirstInquiryReferralDate,
    }) => {
      const dateDiff = existingEpisodeClosedDate
        ? diff(
            existingEpisodeClosedDate,
            formatEpisodeDate(firstInquiryReferralDate),
            'yyyy-LL-dd',
          )
        : diff(
            existingEpisodeFirstInquiryReferralDate,
            formatEpisodeDate(firstInquiryReferralDate),
            'yyyy-LL-dd',
          );
      return (
        (existingEpisodeClosedDate &&
          dateDiff &&
          dateDiff <= 14 &&
          dateDiff >= 0) ||
        (dateDiff <= 0 && dateDiff >= -14)
      );
    },
  );
export const checkForOverlappingPausedRecordDates = (
  currentExternalId: string,
  currentPausedRecordStartDate: string,
  currentPausedRecordEndDate: string,
  pausedDetails: EpisodePausedDetails[],
): boolean =>
  pausedDetails?.some(({ startDate, endDate, externalId }) => {
    //Should not validate against itself
    if (externalId !== currentExternalId) {
      //When two open ended paused records
      if (
        !currentPausedRecordEndDate &&
        !endDate &&
        currentPausedRecordStartDate &&
        startDate
      ) {
        return true;
      }
      //When adding open ended paused records
      if (
        !endDate &&
        currentPausedRecordStartDate &&
        currentPausedRecordEndDate &&
        startDate
      ) {
        return !(
          isDateRangeValidExcludingSameDate(
            currentPausedRecordStartDate,
            startDate,
            'LL/dd/yyyy',
          ) &&
          isDateRangeValidExcludingSameDate(
            currentPausedRecordEndDate,
            startDate,
            'LL/dd/yyyy',
          )
        );
      }
      //When existing open ended paused records but adding both start and end date record
      if (
        !currentPausedRecordEndDate &&
        endDate &&
        currentPausedRecordStartDate &&
        startDate
      ) {
        return !(
          isDateRangeValidExcludingSameDate(
            startDate,
            currentPausedRecordStartDate,
            'LL/dd/yyyy',
          ) &&
          isDateRangeValidExcludingSameDate(
            endDate,
            currentPausedRecordStartDate,
            'LL/dd/yyyy',
          )
        );
      }
      //When adding record with just end date but comparing with existing record having both start and end date record
      if (
        !startDate &&
        endDate &&
        currentPausedRecordStartDate &&
        currentPausedRecordEndDate
      ) {
        return (
          isDateRangeValid(
            currentPausedRecordStartDate,
            endDate,
            DateFormat.longDate, // TODO
          ) &&
          isDateRangeValid(endDate, currentPausedRecordEndDate, 'LL/dd/yyyy')
        );
      }
      //When existing record with just end date but comparing with  record having both start and end date record
      if (
        !currentPausedRecordStartDate &&
        endDate &&
        currentPausedRecordEndDate &&
        startDate
      ) {
        return (
          isDateRangeValid(
            startDate,
            currentPausedRecordEndDate,
            'LL/dd/yyyy',
          ) &&
          isDateRangeValid(currentPausedRecordEndDate, endDate, 'LL/dd/yyyy')
        );
      }
      //When both adding and existing records have both start and end date
      if (
        currentPausedRecordEndDate &&
        endDate &&
        currentPausedRecordStartDate &&
        startDate
      ) {
        return (
          (isDateRangeValid(
            startDate,
            currentPausedRecordStartDate,
            'LL/dd/yyyy',
          ) &&
            isDateRangeValid(
              currentPausedRecordStartDate,
              endDate,
              'LL/dd/yyyy',
            )) ||
          (isDateRangeValid(
            startDate,
            currentPausedRecordEndDate,
            'LL/dd/yyyy',
          ) &&
            isDateRangeValid(
              currentPausedRecordEndDate,
              endDate,
              'LL/dd/yyyy',
            )) ||
          (isDateRangeValid(
            currentPausedRecordStartDate,
            startDate,
            'LL/dd/yyyy',
          ) &&
            isDateRangeValid(
              startDate,
              currentPausedRecordEndDate,
              'LL/dd/yyyy',
            )) ||
          (isDateRangeValid(
            currentPausedRecordStartDate,
            endDate,
            'LL/dd/yyyy',
          ) &&
            isDateRangeValid(endDate, currentPausedRecordEndDate, 'LL/dd/yyyy'))
        );
      }
    }
    return false;
  });

export const getCareTeamId = (
  careTeams: PatientCareTeams,
  type: CareTeamType,
): string => {
  if (type === CareTeamType.ADMISSIONS)
    return careTeams?.admissionsTeam?.externalId ?? null;

  if (type === CareTeamType.CARE)
    return careTeams?.careTeam?.externalId ?? null;

  return null;
};

export function filterOutVoidGoals<
  T extends CarePlanProblemResponse | CarePlanNoteProblemResponse,
>(problems: T[]): T[] {
  return problems
    .map(
      (problem): T => ({
        ...problem,
        goals: problem.goals.filter(
          (goal) => goal.status.code !== CarePlanGoalStatus.VOID,
        ),
      }),
    )
    .filter((p) => p.goals.length > 0);
}

export function filterOutVoidQolGoals<
  T extends CarePlanQolGoalResponse | CarePlanNoteQolGoalResponse,
>(qolGoals: T[]): T[] {
  return qolGoals.filter(
    (goal) => goal.status.code !== CarePlanGoalStatus.VOID,
  );
}

export function filterOutVoidInterventionsFromProblems<
  T extends CarePlanProblemResponse | CarePlanNoteProblemResponse,
>(problems: T[]): T[] {
  return problems.map(
    (problem): T => ({
      ...problem,
      goals: problem.goals.map((goal) => ({
        ...goal,
        interventions: goal.interventions.filter(
          (intervention) =>
            intervention.status.code !== CarePlanInterventionStatus.VOID,
        ),
      })),
    }),
  );
}

export const isDraftNotesConflictError = (error: unknown): boolean =>
  (error as { status?: number })?.status === 409;

export const unwrapRoleGroups = (
  roleGroups: string[],
  providerTypeManifest: ProviderTypeManifestResponse,
): Array<{ code: string; label: string; sortOrder: number }> => {
  if (!roleGroups || roleGroups.length === 0) {
    return [];
  }

  const providerTypeCodesSet = new Set<string>();

  roleGroups.forEach((groupCode) => {
    const providerTypeCodes = providerTypeManifest.groupMappings[groupCode];
    if (providerTypeCodes) {
      providerTypeCodes.forEach((code) => providerTypeCodesSet.add(code));
    }
  });

  return Array.from(providerTypeCodesSet)
    .map((code) => {
      return providerTypeManifest.providerTypes.find((pt) => pt.code === code);
    })
    .filter(
      (pt): pt is { code: string; label: string; sortOrder: number } =>
        pt !== undefined,
    );
};

// The BE returns only interventions assigned to the viewer's role.
const hasRoleAssignedIntervention = (
  problem: CarePlanNoteProblemResponse,
): boolean => problem.goals.some((goal) => goal.interventions.length > 0);

// coOccurringProblems holds only the problems with a role-assigned intervention;
// hiddenCoOccurringCount counts the rest.
export const partitionProblemsForNote = ({
  problems,
  isMedPsychProblemsEnabled,
  compare,
  prefilter,
}: {
  problems: CarePlanNoteProblemResponse[];
  isMedPsychProblemsEnabled: boolean;
  compare: (
    a: CarePlanNoteProblemResponse,
    b: CarePlanNoteProblemResponse,
  ) => number;
  prefilter?: (problem: CarePlanNoteProblemResponse) => boolean;
}): {
  edProblems: CarePlanNoteProblemResponse[];
  coOccurringProblems: CarePlanNoteProblemResponse[];
  hiddenCoOccurringCount: number;
} => {
  const sorted = (prefilter ? problems.filter(prefilter) : problems).toSorted(
    compare,
  );

  if (!isMedPsychProblemsEnabled) {
    return {
      edProblems: sorted,
      coOccurringProblems: [],
      hiddenCoOccurringCount: 0,
    };
  }

  const edProblems: CarePlanNoteProblemResponse[] = [];
  const coOccurringProblems: CarePlanNoteProblemResponse[] = [];
  sorted.forEach((problem) => {
    if (
      !problem.problemType ||
      problem.problemType === ProblemTypeCode.EATING_DISORDER
    ) {
      edProblems.push(problem);
    } else {
      coOccurringProblems.push(problem);
    }
  });
  const visibleCoOccurringProblems = coOccurringProblems.filter(
    hasRoleAssignedIntervention,
  );
  return {
    edProblems,
    coOccurringProblems: visibleCoOccurringProblems,
    hiddenCoOccurringCount:
      coOccurringProblems.length - visibleCoOccurringProblems.length,
  };
};

export const mapRoleCodesToLabels = (
  roleCodes: string[],
  providerTypeManifest: ProviderTypeManifestResponse,
): string[] => {
  return roleCodes.map((roleCode) => {
    const providerType = providerTypeManifest.providerTypes.find(
      (pt) => pt.code === roleCode,
    );
    return providerType ? providerType.label : roleCode;
  });
};
